/**
 * TextButton component - shared types and Zod prop schema.
 *
 * A text-only button for secondary actions where the visual treatment is
 * button text in a minimum touch target, not a filled pill shape.
 */

import { z } from 'zod';
import { actionIdSchema } from '@galapagos/product-analytics';
import { colors } from '../../elements/colors';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';
import { typography } from '../../elements/typography';

export const TextButtonState = z.enum(['Default', 'Disabled']);
export type TextButtonState = z.infer<typeof TextButtonState>;

/**
 * Visual form of the pressable text. `Button` is the centered button-text
 * treatment; `Link` is an underlined body-text run for inline "learn more"
 * affordances that sit inside flowing copy. Both keep the minimum touch target.
 */
export const TextButtonType = z.enum(['Button', 'Link']);
export type TextButtonType = z.infer<typeof TextButtonType>;

export const textButtonPropsSchema = z.object({
  label: z.string().min(1),
  type: TextButtonType.default('Button'),
  state: TextButtonState.default('Default'),
  accessibilityLabel: z.string().optional(),
  testID: z.string().optional(),
  /** Domain action id for the activation; the component falls back to a generic one. */
  actionId: actionIdSchema.optional(),
});

export type TextButtonProps = z.input<typeof textButtonPropsSchema>;

export const TEXT_BUTTON_TYPOGRAPHY = typography.button1;
export const TEXT_BUTTON_LINK_TYPOGRAPHY = typography.b2Regular;
export const TEXT_BUTTON_MIN_HEIGHT = sizing.touchTarget.minimum;
export const TEXT_BUTTON_MIN_WIDTH = sizing.touchTarget.minimum;
export const TEXT_BUTTON_VERTICAL_PADDING = spacing.s;
export const TEXT_BUTTON_HORIZONTAL_PADDING = spacing.s;

export const textButtonTypographyForType: Record<
  TextButtonType,
  (typeof typography)[keyof typeof typography]
> = {
  Button: TEXT_BUTTON_TYPOGRAPHY,
  Link: TEXT_BUTTON_LINK_TYPOGRAPHY,
};

export const textButtonColorForState: Record<TextButtonState, string> = {
  Default: colors.text.primary,
  Disabled: colors.text.disabled,
};
